import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../api/api';
import { Users as UsersIcon, Search, Shield, ShieldOff, FileText, Building2, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Card, Btn, Badge, PlanBadge, Modal, formatDate, Spinner } from '../components/admin/AdminUI';
import { useAuth } from '../context/AuthContext';

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { document.title = "Skilldeck — Admin · Utilisateurs"; }, []);
  useEffect(() => { const t = setTimeout(() => setSearchDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { users: list } = await adminAPI.listUsers({ search: searchDebounced || undefined, limit: 100 });
      setUsers(list);
    } catch { toast({ title: 'Erreur', description: 'Impossible de charger les utilisateurs', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [searchDebounced, toast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const d = await adminAPI.getUser(userId);
      setDetail(d);
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
    finally { setDetailLoading(false); }
  };

  const togglePlatformAdmin = async (u) => {
    if (u.id === currentUser?.id && u.is_platform_admin) {
      toast({ title: 'Action interdite', description: 'Vous ne pouvez pas retirer votre propre statut.', variant: 'destructive' });
      return;
    }
    const next = !u.is_platform_admin;
    const msg = next ? 'Donner les droits super admin à ' : 'Retirer les droits super admin de ';
    if (!window.confirm(msg + (u.full_name || u.email || 'cet utilisateur') + ' ?')) return;
    try {
      await adminAPI.setPlatformAdmin(u.id, next);
      toast({ title: next ? 'Super admin accordé' : 'Super admin retiré' });
      load();
      if (detail?.profile?.id === u.id) setDetail((d) => ({ ...d, profile: { ...d.profile, is_platform_admin: next } }));
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Échec', variant: 'destructive' }); }
  };

  return (
    <AdminLayout title="Utilisateurs">
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">

        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (nom, email)…"
                className="w-full pl-9 pr-4 py-2 bg-[#05050f] border border-[rgba(108,71,255,.15)] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/50" />
            </div>
            <div className="ml-auto text-xs text-gray-500">{users.length} résultats</div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <UsersIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>Aucun utilisateur</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[rgba(108,71,255,.1)] text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    <th className="px-5 py-3">Utilisateur</th>
                    <th className="px-5 py-3">Email</th>
                    <th className="px-5 py-3">Orgs</th>
                    <th className="px-5 py-3">Dossiers</th>
                    <th className="px-5 py-3">Rôle plateforme</th>
                    <th className="px-5 py-3">Inscription</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(108,71,255,.06)]">
                  {users.map((u) => {
                    const initials = (u.full_name || u.email || '?').split(/\s|@/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
                    return (
                      <tr key={u.id} className="hover:bg-[rgba(108,71,255,.04)] transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-violet-900/30 flex items-center justify-center text-violet-200 font-semibold text-xs shrink-0">{initials}</div>
                            <div>
                              <p className="text-sm font-medium text-white">{u.full_name || '—'}</p>
                              <p className="text-[11px] text-gray-500 font-mono">{u.id.slice(0, 12)}…</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-300">{u.email || '—'}</td>
                        <td className="px-5 py-3 text-sm text-gray-300">{u.org_count}</td>
                        <td className="px-5 py-3 text-sm text-gray-300">{u.dossier_count}</td>
                        <td className="px-5 py-3">
                          {u.is_platform_admin ? <Badge tone="violet"><Shield className="h-3 w-3 mr-1" />Super admin</Badge> : <Badge tone="gray">Utilisateur</Badge>}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-400">{formatDate(u.created_at)}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <Btn tone="subtle" onClick={() => openDetail(u.id)}>Détails</Btn>
                            <Btn tone={u.is_platform_admin ? 'danger' : 'subtle'} onClick={() => togglePlatformAdmin(u)}>
                              {u.is_platform_admin ? <><ShieldOff className="h-3 w-3" /> Retirer</> : <><Shield className="h-3 w-3" /> Promouvoir</>}
                            </Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Detail modal */}
        <Modal open={!!detail || detailLoading} onClose={() => setDetail(null)} title={detail?.profile?.full_name || 'Utilisateur'} maxWidth="max-w-2xl">
          {detailLoading || !detail ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <div className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="Email" value={detail.profile.email || '—'} />
                <Field label="Nom complet" value={detail.profile.full_name || '—'} />
                <Field label="Inscription" value={formatDate(detail.profile.created_at)} />
                <Field label="Dossiers créés" value={detail.dossier_count} />
                <Field label="UUID" value={<span className="font-mono text-[11px]">{detail.profile.id}</span>} />
                <Field label="Statut" value={detail.profile.is_platform_admin ? <Badge tone="violet">Super admin</Badge> : <Badge tone="gray">Utilisateur</Badge>} />
              </div>

              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Organisations ({detail.memberships.length})</p>
                {detail.memberships.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune appartenance</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.memberships.map((m) => (
                      <li key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-[rgba(108,71,255,.12)] bg-[rgba(108,71,255,.03)]">
                        <div className="flex items-center gap-3 min-w-0">
                          <Building2 className="h-4 w-4 text-violet-300 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate">{m.organizations?.name || m.organization_id}</p>
                            <p className="text-[11px] text-gray-500 font-mono truncate">{m.organizations?.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <PlanBadge code={m.organizations?.plan_code} />
                          <Badge tone="blue">{m.role}</Badge>
                          <Badge tone="gray">{m.status}</Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-between pt-2">
                <Btn tone={detail.profile.is_platform_admin ? 'danger' : 'subtle'}
                  onClick={() => togglePlatformAdmin({ id: detail.profile.id, ...detail.profile })}>
                  {detail.profile.is_platform_admin ? <><ShieldOff className="h-3 w-3" /> Retirer super admin</> : <><Shield className="h-3 w-3" /> Promouvoir super admin</>}
                </Btn>
                <Btn tone="ghost" onClick={() => setDetail(null)}><X className="h-3 w-3" /> Fermer</Btn>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AdminLayout>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-sm text-gray-200 mt-0.5">{value}</p>
  </div>
);

export default AdminUsers;
